import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ArrowUpRight, RefreshCw } from "lucide-react";
import { dealerApi } from "../lib/api";
import { useDealerSession } from "../lib/session";
import { dateTime, tzs } from "../lib/format";
import { OrderPill } from "../components/Status";

interface DashboardData {
  wallet: { available_minor: number; reserved_minor: number; commission_minor: number };
  agents_total: number;
  agents_active: number;
  transferred_today_minor: number;
  commission_month_minor: number;
  commission_total_minor: number;
  dealer: { code: string; business_name: string; txn_transfer_limit_minor: number; daily_transfer_limit_minor: number };
}

interface TransferRow {
  ref: string;
  description: string | null;
  posted_at: string | null;
}

interface OrderRow {
  id: string;
  msisdn: string | null;
  mno_code: string;
  amount_minor: number;
  status: string;
  created_at: string | null;
}

export function Dashboard() {
  const { dealer } = useDealerSession();
  const dash = useQuery({
    queryKey: ["dealer-dashboard"],
    queryFn: () => dealerApi.get<DashboardData>("/superdealer/v1/dashboard"),
    refetchInterval: 60_000,
  });
  const transfers = useQuery({
    queryKey: ["dealer-transfers", 6],
    queryFn: () =>
      dealerApi.get<{ data: TransferRow[]; total: number }>("/superdealer/v1/transfers?limit=6"),
  });
  const sales = useQuery({
    queryKey: ["dealer-sales", 6],
    queryFn: () =>
      dealerApi.get<{ data: OrderRow[]; total: number }>("/superdealer/v1/transactions?limit=6"),
  });

  const d = dash.data;
  const dailyUsedPct =
    d && d.dealer.daily_transfer_limit_minor > 0
      ? Math.min(100, Math.round((d.transferred_today_minor / d.dealer.daily_transfer_limit_minor) * 100))
      : 0;

  return (
    <div className="dash-page">
      <div className="dash-head">
        <div className="dash-head-title">
          <div>
            <h2>Welcome back, {dealer?.business_name ?? "Dealer"}</h2>
            <p className="ev-body-muted">Float distribution and commission overview.</p>
          </div>
        </div>
        <div className="dash-head-controls">
          <button
            className="ev-icon-btn"
            aria-label="Refresh"
            onClick={() => {
              void dash.refetch();
              void transfers.refetch();
              void sales.refetch();
            }}
          >
            <RefreshCw size={16} />
          </button>
          <Link to="/send" className="ev-btn ev-btn-primary ev-btn-sm">
            <SendIcon />
            Send float
          </Link>
        </div>
      </div>

      <div className="dash-kpi-row">
        <div className="dash-kpi dash-kpi-navy">
          <span className="dash-kpi-label">Available float</span>
          <div className="dash-kpi-body">
            <span className="dash-kpi-value">{tzs(d?.wallet.available_minor)}</span>
          </div>
          <span className="dash-kpi-note">
            Per-transfer limit {tzs(d?.dealer.txn_transfer_limit_minor ?? 10_000_000)}
          </span>
        </div>
        <div className="dash-kpi dash-kpi-teal">
          <span className="dash-kpi-label">Sent today</span>
          <div className="dash-kpi-body">
            <span className="dash-kpi-value">{tzs(d?.transferred_today_minor)}</span>
          </div>
          <span className="dash-kpi-note">{dailyUsedPct}% of the daily limit used</span>
        </div>
        <div className="dash-kpi dash-kpi-green">
          <span className="dash-kpi-label">Commission this month</span>
          <div className="dash-kpi-body">
            <span className="dash-kpi-value">{tzs(d?.commission_month_minor)}</span>
          </div>
          <span className="dash-kpi-note">{tzs(d?.commission_total_minor)} earned to date</span>
        </div>
        <div className="dash-kpi dash-kpi-violet">
          <span className="dash-kpi-label">My agents</span>
          <div className="dash-kpi-body">
            <span className="dash-kpi-value">{d?.agents_total ?? 0}</span>
          </div>
          <span className="dash-kpi-note">{d?.agents_active ?? 0} active</span>
        </div>
      </div>

      <div className="dash-tables">
        <div className="dash-table-card">
          <div className="dash-card-head">
            <h3 className="dash-card-title">Recent float transfers</h3>
            <Link className="dash-link" to="/send">
              Send float <ArrowUpRight size={14} />
            </Link>
          </div>
          <div className="dash-table-wrap">
            <table className="dash-table">
              <thead>
                <tr>
                  <th>Reference</th>
                  <th>Description</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {(transfers.data?.data ?? []).map((t) => (
                  <tr key={t.ref}>
                    <td>
                      <span className="dash-mono">{t.ref.slice(0, 13)}</span>
                    </td>
                    <td>
                      <div className="dash-cell-title">{t.description ?? "Float transfer"}</div>
                    </td>
                    <td className="ev-body-micro muted">{dateTime(t.posted_at)}</td>
                  </tr>
                ))}
                {!transfers.data?.data.length && (
                  <tr>
                    <td colSpan={3}>
                      <div className="dash-table-empty">No float transferred yet.</div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="dash-table-card">
          <div className="dash-card-head">
            <h3 className="dash-card-title">Recent network sales</h3>
            <Link className="dash-link" to="/transactions">
              All transactions <ArrowUpRight size={14} />
            </Link>
          </div>
          <div className="dash-table-wrap">
            <table className="dash-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Recipient</th>
                  <th>Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {(sales.data?.data ?? []).map((o) => (
                  <tr key={o.id}>
                    <td>
                      <span className="dash-mono">{o.id}</span>
                    </td>
                    <td>
                      <div className="dash-cell-title">{o.msisdn ?? "—"}</div>
                      <div className="dash-cell-sub">{o.mno_code}</div>
                    </td>
                    <td>{tzs(o.amount_minor)}</td>
                    <td>
                      <OrderPill status={o.status} />
                    </td>
                  </tr>
                ))}
                {!sales.data?.data.length && (
                  <tr>
                    <td colSpan={4}>
                      <div className="dash-table-empty">No sales in your network yet.</div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function SendIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 6 }}>
      <path d="M5 12h14M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
